import {
  EApplicationFilter,
  IFilterCriteria,
} from "@rentiohq/shared-frontend/dist/reduxV2/applicationFilters/types";
import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { isPetResident } from "utils/application.utils";
import { getApplicationIndicators } from "./components/ApplicationTable/applicationIndicators.utils";
import {
  getResidentsCount,
  getTotalIncome,
} from "./components/DragItem/dragItem.utils";

/** The order of the "+ Filter" list and of the panel. */
export const ALL_APPLICATION_FILTERS: EApplicationFilter[] = [
  EApplicationFilter.Income,
  EApplicationFilter.RentMatchScore,
  EApplicationFilter.Occupants,
  EApplicationFilter.Rating,
  EApplicationFilter.Applications,
  EApplicationFilter.Pets,
  EApplicationFilter.StartDate,
  EApplicationFilter.DocumentsComplete,
  EApplicationFilter.NoNoShowsElsewhere,
];

/**
 * What a broker who never picked a set sees: the five filters the panel always
 * showed before DEV-3543, so nothing disappears on the day this ships.
 */
export const DEFAULT_VISIBLE_FILTERS: EApplicationFilter[] = [
  EApplicationFilter.Income,
  EApplicationFilter.RentMatchScore,
  EApplicationFilter.Occupants,
  EApplicationFilter.Rating,
  EApplicationFilter.Applications,
];

type TCriteriaField = Exclude<keyof IFilterCriteria, "visibleFilters">;

/** The criteria fields each filter owns, cleared together when it is hidden. */
export const FILTER_CRITERIA_FIELDS: Record<
  EApplicationFilter,
  TCriteriaField[]
> = {
  [EApplicationFilter.Income]: ["incomeMultiplier"],
  [EApplicationFilter.RentMatchScore]: ["rentMatchMinScore"],
  [EApplicationFilter.Occupants]: ["maxAdults", "maxChildren"],
  [EApplicationFilter.Applications]: ["minApplications", "maxApplications"],
  [EApplicationFilter.Rating]: ["minRating"],
  [EApplicationFilter.Pets]: ["pets"],
  [EApplicationFilter.StartDate]: ["startDateDirection", "startDate"],
  [EApplicationFilter.DocumentsComplete]: ["documentsComplete"],
  [EApplicationFilter.NoNoShowsElsewhere]: ["noNoShowsElsewhere"],
};

const isApplicationFilter = (value: unknown): value is EApplicationFilter =>
  ALL_APPLICATION_FILTERS.includes(value as EApplicationFilter);

const hasValue = (value: unknown) =>
  value != null && value !== "" && value !== false;

/** The filters that hold a value in these criteria. */
export const getFiltersWithValue = (criteria: IFilterCriteria) =>
  ALL_APPLICATION_FILTERS.filter(filter =>
    FILTER_CRITERIA_FIELDS[filter].some(field => hasValue(criteria[field])),
  );

/** How many filters narrow the list; the panel's own bookkeeping is not one. */
export const countActiveFilters = (criteria: IFilterCriteria) =>
  getFiltersWithValue(criteria).length;

/**
 * The filters a saved filter shows. A filter saved before DEV-3543 has no
 * `visibleFilters` and shows the filters its values belong to; a filter that
 * holds a value is always shown, so nothing filters the list unseen.
 */
export const getVisibleFiltersForCriteria = (criteria: IFilterCriteria) => {
  const stored = (criteria.visibleFilters || []).filter(isApplicationFilter);
  const withValue = getFiltersWithValue(criteria);

  return ALL_APPLICATION_FILTERS.filter(
    filter => stored.includes(filter) || withValue.includes(filter),
  );
};

/** Hides a filter: clears every field it owns, so it stops filtering. */
export const clearFilterCriteria = (
  criteria: IFilterCriteria,
  filter: EApplicationFilter,
): IFilterCriteria => {
  const next = { ...criteria };
  FILTER_CRITERIA_FIELDS[filter].forEach(field => {
    delete next[field];
  });
  return next;
};

const VISIBLE_FILTERS_STORAGE_KEY = "rentmatch-application-visible-filters";

/**
 * The set the broker turned on, kept in this browser only (decided by Jonas on
 * 2026-10-23): no server state, so another computer starts from the default
 * until the broker saves the set as a saved filter. Storage can throw in a
 * private window; this is a convenience and falls back to the default.
 */
export const readVisibleFilters = (): EApplicationFilter[] => {
  try {
    const raw = window.localStorage.getItem(VISIBLE_FILTERS_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_VISIBLE_FILTERS;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return DEFAULT_VISIBLE_FILTERS;
    }
    return ALL_APPLICATION_FILTERS.filter(filter => parsed.includes(filter));
  } catch {
    return DEFAULT_VISIBLE_FILTERS;
  }
};

export const writeVisibleFilters = (filters: EApplicationFilter[]) => {
  try {
    window.localStorage.setItem(
      VISIBLE_FILTERS_STORAGE_KEY,
      JSON.stringify(filters),
    );
  } catch {
    // Full, blocked or unavailable. The panel still works for this visit.
  }
};

/** A calendar day as the start date filter stores it. */
export const toFilterDay = (date: Date) => formatDate(date, "yyyy-MM-dd");

/**
 * The day the start date filter starts from: the property's availability date
 * when it can be read, today otherwise.
 */
export const getDefaultStartDate = (availableAtDate?: string | Date) => {
  const parsed = availableAtDate ? new Date(availableAtDate) : null;
  return toFilterDay(
    parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date(),
  );
};

/**
 * "No pets" needs a pets answer of zero, not the absence of one: a candidacy
 * that never carried the question (a visit request under the Brussels-law
 * setting, DEV-3522) is found by neither option.
 */
const matchesPets = (
  application: IApplication,
  pets: NonNullable<IFilterCriteria["pets"]>,
) => {
  const petResidents = (application.residents || []).filter(resident =>
    isPetResident(resident.id),
  );
  const petCount = petResidents.reduce(
    (total, resident) => total + resident.value,
    0,
  );

  return pets === "some"
    ? petCount > 0
    : petResidents.length > 0 && petCount === 0;
};

/**
 * Required comes from the row (DEV-3135). Null is "we do not know" on rows
 * from before that contract, so such a row is left out rather than read as
 * complete; zero is "nothing was asked" and is complete.
 */
const hasCompleteDocuments = (application: IApplication) => {
  const { idDocs, incomeDocs } = getApplicationIndicators(application);

  return [idDocs, incomeDocs].every(
    ({ required, delivered }) => required != null && delivered >= required,
  );
};

const matchesStartDate = (
  application: IApplication,
  direction: NonNullable<IFilterCriteria["startDateDirection"]>,
  day: string,
) => {
  // Unreadable dates (the MySQL zero date) arrive as null, and a candidate
  // without a readable date is left out while this filter is on.
  const { moveInDate } = getApplicationIndicators(application);
  if (!moveInDate) {
    return false;
  }

  // "yyyy-MM-dd" compares as a string, and comparing days rather than moments
  // keeps a candidate who wants the chosen day itself on both sides.
  const moveInDay = toFilterDay(moveInDate);
  return direction === "onOrBefore" ? moveInDay <= day : moveInDay >= day;
};

/**
 * The candidate list's filters, run on the rows already loaded. Every filter
 * without a value lets every row through.
 */
export const applyFilterCriteria = (params: {
  applications: IApplication[];
  criteria: IFilterCriteria;
  propertyPrice?: number;
  getRentMatchScore: (application: IApplication) => number | null | undefined;
}) => {
  const { criteria, propertyPrice, getRentMatchScore } = params;
  let items = params.applications;

  if (criteria.incomeMultiplier) {
    const minIncome = (propertyPrice || 0) * criteria.incomeMultiplier;
    items = items.filter(item => getTotalIncome(item.contacts) >= minIncome);
  }
  if (criteria.maxAdults) {
    const { maxAdults } = criteria;
    items = items.filter(item => {
      const adults =
        getResidentsCount(item.residents, "INTAKE_RESIDENT_ADULTS") ||
        item.contacts.length;
      return adults <= maxAdults;
    });
  }
  // `maxChildren` may legitimately be 0 (candidates without children), so
  // check against null instead of a truthy check that would skip 0.
  if (criteria.maxChildren != null) {
    const { maxChildren } = criteria;
    items = items.filter(
      item =>
        getResidentsCount(item.residents, "INTAKE_RESIDENT_CHILDREN") <=
        maxChildren,
    );
  }

  // Applications in total, this one included. "More than N" is what the filter
  // always did; "at most N" was added beside it (DEV-3543). The same field the
  // row's "candidate for N properties" badge shows, so the two always agree.
  const getTotalApplications = (item: IApplication) =>
    item.otherApplicationsTotalCount ?? 0;
  // A count may be 0, which the panel counts as an active filter; check
  // against null so the list is filtered whenever the badge says it is.
  if (criteria.minApplications != null) {
    const { minApplications } = criteria;
    items = items.filter(item => getTotalApplications(item) > minApplications);
  }
  if (criteria.maxApplications != null) {
    const { maxApplications } = criteria;
    items = items.filter(item => getTotalApplications(item) <= maxApplications);
  }

  if (criteria.minRating) {
    const { minRating } = criteria;
    items = items.filter(item => (item.rating || 0) >= minRating);
  }
  if (criteria.rentMatchMinScore) {
    const { rentMatchMinScore } = criteria;
    items = items.filter(item => {
      const score = getRentMatchScore(item);
      return score != null && score >= rentMatchMinScore;
    });
  }
  if (criteria.pets) {
    const { pets } = criteria;
    items = items.filter(item => matchesPets(item, pets));
  }
  if (criteria.startDateDirection && criteria.startDate) {
    const { startDateDirection, startDate } = criteria;
    items = items.filter(item =>
      matchesStartDate(item, startDateDirection, startDate),
    );
  }
  if (criteria.documentsComplete) {
    items = items.filter(hasCompleteDocuments);
  }
  // Despite its stored name, this hides a no-show at this property as well as
  // one at another property of the office (Jonas, 2026-10-28). The name stays,
  // so saved filters keep working.
  if (criteria.noNoShowsElsewhere) {
    items = items.filter(
      item => !item.noShowAt && !item.otherApplicationsMeta?.noShows?.length,
    );
  }

  return items;
};
